import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, Observable, of, shareReplay, tap } from 'rxjs';
import { GridLayout } from 'src/app/classes/widget/dashboard-grid';
import { CompanyDashboard, PostDashboard, PutDashboard, SynthesisTheme } from 'src/app/interfaces/api/dashboard';
import { Dashboard } from 'src/app/interfaces/shared/dashboard';
import { DashboardStore } from 'src/app/services/stores/dashboard.store';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../core/api.service';
import { OfflineRequestService } from '../../core/offline-request.service';
import { RequestService } from '../../core/request.service';
import { WebSocketService } from '../../core/web-socket.service';

type QueryParams = Record<string, string | number | boolean | Date | Array<string | number | boolean | Date> | object>;

@Injectable({
    providedIn: 'root'
})

export class DashboardService {
    // Services
    public readonly request: RequestService = inject(RequestService);
    public readonly socket: WebSocketService = inject(WebSocketService)
    public readonly http: HttpClient = inject(HttpClient)
    public readonly offlineService: OfflineRequestService = inject(OfflineRequestService)
    private readonly apiService: ApiService = inject(ApiService)
    private readonly dashboardStore: DashboardStore = inject(DashboardStore)

    // Variables
    collection = "Dashboard";
    collectionPath = "dashboards";
    projects: Dashboard[] = [];
    // Dashboards request in flight per project: shared by concurrent loads, and kept going when its caller leaves.
    private readonly pending = new Map<string, Observable<Dashboard[]>>();
    apiUrl = environment.API_URL;

    constructor() {
        this.listenToUpdates();
        this.listenToChartUpdates();
    }

    //---GET---//

    getDashboards(query: QueryParams = {}, forceRefresh: boolean = false): Observable<Dashboard[]> {
        const projectId = Array.isArray(query['projects']) ? query['projects'][0] : query['projects'];

        if (!projectId) {
            console.warn('⚠️ getDashboards called without projectId');
            return of([]);
        }

        const id = String(projectId);
        if (!forceRefresh) {
            if (this.dashboardStore.isProjectLoaded(id)) return of(this.dashboardStore.sortedDashboards());
            const pending = this.pending.get(id);
            if (pending) return pending;
        }

        const request = this.request.getCollection(this.collection, this.collectionPath, query).pipe(
            tap((dashboards: Dashboard[]) => {
                // A late answer for a project left meanwhile does not replace the current one.
                const current = this.dashboardStore.currentProjectId();
                if (!current || current === id) this.dashboardStore.setDashboards(dashboards, id);
            }),
            catchError((err) => {
                console.error('❌ Error loading dashboards:', err);
                throw err;
            }),
            finalize(() => {
                if (this.pending.get(id) === request) this.pending.delete(id);
                if (!this.pending.size) this.dashboardStore.setLoading(false);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        this.pending.set(id, request);
        this.dashboardStore.setLoading(true);
        return request;
    }

    getDashboard(id: string): Observable<any> {
        return this.request.getItem(this.collection, this.collectionPath, id)
    }

    getPersonalDashboard(query: QueryParams = {}): Observable<any> {
        return this.apiService.getCollection(this.collectionPath, "", query);
    }

    getDashboardList(query: QueryParams = {}): Observable<any> {
        return this.apiService.getCollection(this.collectionPath, "", query);
    }

    mailReports(query: QueryParams = {}) {
        return this.apiService.getCollection(this.collectionPath, 'report', query);
    }

    // Synthesis dashboard of a company theme: no project cache nor socket sync.
    getCompanyDashboard(companyId: string, theme: SynthesisTheme): Observable<CompanyDashboard> {
        return this.apiService.getCollection(this.collectionPath, `company/${companyId}/${theme}`);
    }

    // Both return the whole synthesis dashboard.
    addCompanyChart(companyId: string, theme: SynthesisTheme, chartId: string): Observable<CompanyDashboard> {
        return this.apiService.updateCollection(this.collectionPath, `company/${companyId}/${theme}/addChart`, { chartId });
    }

    removeCompanyChart(companyId: string, theme: SynthesisTheme, chartId: string): Observable<CompanyDashboard> {
        return this.apiService.updateCollection(this.collectionPath, `company/${companyId}/${theme}/removeChart`, { chartId });
    }

    saveCompanyLayout(companyId: string, theme: SynthesisTheme, layout: GridLayout): Observable<{ id: string, layout: GridLayout }> {
        return this.apiService.updateCollection(this.collectionPath, `company/${companyId}/${theme}/layout`, { layout });
    }

    //---POST---//

    addDashboard(dashboard: PostDashboard): Observable<any> {
        return this.request.postItem(this.collection, this.collectionPath, dashboard).pipe(
            tap((newDashboard: Dashboard) => {
                this.dashboardStore.addDashboard(newDashboard);
            })
        );
    }

    duplicateDashboard(id: string): Observable<any> {
        return this.apiService.getCollectionItem(this.collectionPath, "duplicate", id).pipe(
            tap((dashboard: Dashboard) => {
                this.dashboardStore.addDashboard(dashboard);
            })
        );
    }

    instantiateDashboard(id: string): Observable<any> {
        return this.apiService.getCollectionItem(this.collectionPath, "instantiate", id).pipe(
            tap((dashboard: Dashboard) => {
                this.dashboardStore.addDashboard(dashboard);
            })
        );
    }

    //---PUT---//

    updateDashboards(dashboards: PutDashboard[]): Observable<any> {
        return this.request.setProjectDataWithIdBody(this.collection, this.collectionPath, dashboards).pipe(
            tap((updatedDashboards: Dashboard[]) => {
                this.dashboardStore.mergeDashboards(updatedDashboards);
            })
        );
    }

    updateDashboard(dashboard: PutDashboard, withCharts: boolean = true): Observable<any> {
        return this.apiService.updateCollectionItem(this.collectionPath, dashboard).pipe(
            tap((updatedDashboard: Dashboard) => {
                if (withCharts) {
                    this.dashboardStore.updateDashboard(updatedDashboard);
                } else {
                    const { charts, ...dashboardWithoutCharts } = updatedDashboard;
                    this.dashboardStore.updateDashboard(dashboardWithoutCharts as any);
                }
            })
        );
    }

    /**
     * $addToSet/$pull côté serveur au lieu d'envoyer le tableau `charts` complet
     * recalculé côté client : deux ajouts/retraits concurrents sur le même
     * dashboard ne s'écrasent plus jamais, peu importe l'ordre d'arrivée.
     */
    addChartToDashboard(dashboardId: string, chartId: string): Observable<any> {
        return this.apiService.updateCollection(this.collectionPath, `${dashboardId}/addChart`, { chartId }).pipe(
            tap((updatedDashboard: Dashboard) => {
                const { charts, ...dashboardWithoutCharts } = updatedDashboard;
                this.dashboardStore.updateDashboard(dashboardWithoutCharts as any);
            })
        );
    }

    removeChartFromDashboard(dashboardId: string, chartId: string): Observable<any> {
        return this.apiService.updateCollection(this.collectionPath, `${dashboardId}/removeChart`, { chartId }).pipe(
            tap((updatedDashboard: Dashboard) => {
                const { charts, ...dashboardWithoutCharts } = updatedDashboard;
                this.dashboardStore.updateDashboard(dashboardWithoutCharts as any);
            })
        );
    }

    // Whole layout at once.
    saveLayout(dashboardId: string, layout: GridLayout): Observable<{ id: string, layout: GridLayout }> {
        return this.apiService.updateCollection(this.collectionPath, `${dashboardId}/layout`, { layout }).pipe(
            tap((saved: { id: string, layout: GridLayout }) => this.dashboardStore.updateDashboard({ id: dashboardId, layout: saved.layout }))
        );
    }

    orderUpdateDashboards(payload: { projectId: string, dashboards: PutDashboard[] }): Observable<any> {
        return this.apiService.updateCollection(this.collectionPath, 'order', payload).pipe(
            tap((partialDashboards: Dashboard[]) => {
                this.dashboardStore.mergeDashboards(partialDashboards);
            })
        );
    }

    //---DELETE---//

    removeDashboard(id: string, allProjects: boolean = false): Observable<any> {
        return this.request.deleteItem(this.collection, this.collectionPath, id, allProjects).pipe(
            tap(() => {
                this.dashboardStore.removeDashboard(id);
            })
        );
    }

    //---AUTRES---//

    /**
     * 📡 Écoute les mises à jour WebSocket pour synchronisation temps réel
     */
    private listenToUpdates(): void {
        this.socket.getUpdateRaw(this.collection).subscribe({
            next: (update: any) => {

                if (!update.data) {
                    console.warn('⚠️ WebSocket update with no data');
                    return;
                }

                const data = Array.isArray(update.data) ? update.data : [update.data];

                switch (update.method) {
                    case 'DELETE':
                        const ids = data.map((d: any) => d.id || d._id);
                        this.dashboardStore.removeDashboard(ids);
                        return;

                    case 'PUT_BATCH':
                        this.dashboardStore.mergeDashboards(data);
                        break;

                    case 'POST':
                    case 'PUT':
                        this.dashboardStore.upsertDashboards(data);
                        break;

                    default:
                        console.warn('⚠️ Unknown WebSocket method:', update.method);
                        return;
                }

                data.forEach((d: any) => this.dashboardStore.syncWidgetsForRealtimeUpdate(d));
            },
            error: (err) => console.error('❌ WebSocket update error:', err)
        });
    }

    /**
     * 📡 Écoute les mises à jour de la collection Chart
     */
    private listenToChartUpdates(): void {
        this.socket.getUpdateRaw('Chart').subscribe({
            next: (update: any) => {
                if (!update.data || update.method === 'DELETE') {
                    return;
                }

                const data = Array.isArray(update.data) ? update.data : [update.data];
                data.forEach((chart: any) => this.dashboardStore.mergeChartUpdate(chart));
            },
            error: (err) => console.error('❌ WebSocket Chart update error:', err)
        });
    }
}
